import React from 'react';
import ReactDOM from 'react-dom';

import { applyMiddleware, compose, createStore } from 'redux';
import thunkMiddleware from 'redux-thunk';
import { formatDefaultLocale } from 'd3-format';
import actionCreators from './actions.js';
import AppProvider from './components/AppProvider/AppProvider.tsx';
import rootReducer from './reducer.js';
import { DEFAULT_MARKETING_FOCUS } from './realEstateTypes.js';
import { DEFAULT_USER_INTENT } from './userIntents.js';
import { getMarketingFocusUpdater } from './utils/marketingFocusUpdater.js';
import { getUserIntentUpdater } from './utils/userIntentUpdater.js';
import { getReportPageViewWithGeoData } from './utils/pageViewWithGeoDataReporter.js';
import GeoHierarchyPage from './components/GeoHierarchyPage/GeoHierarchyPage.jsx';

import './assets/styles/footer.less';
import './assets/styles/slick.less';
import './assets/styles/slick-theme.less';

import { doesHashMatchTab, getDocumentReferer } from './utils/urlUtils.js';
import { getPageViewId } from './selectors/selectors';
import featureSwitchReader from './utils/featureSwitchReader';
import { TAB } from "./components/Navigation/Menu/menuConfig";

featureSwitchReader.set(window.IS24.PropertyBook.featureSwitches);
const enableHydrateForSSRRoute = (hash) => (doesHashMatchTab(hash, TAB.home) || hash === '#/' || hash === '');
const currentHash = window.location.hash;

formatDefaultLocale({
  // d3 settings
  decimal: ',',
  thousands: '.',
  grouping: [3],
  currency: ['', '\u00a0€'],
});

const trackingContextData = ({ getState }) => (next) => (action) => {
  const returnValue = next(action);
  try {
    const oldContextData = window.IS24.TEALIUM.tracking.getContextData();
    window.IS24.TEALIUM.tracking.overwriteContextData({
      ...oldContextData,
      pageViewId: getPageViewId(getState()),
    });
  } catch (error) {
    // eslint-disable-next-line
  }
  return returnValue;
};

const middlewares = [thunkMiddleware, trackingContextData];

let enhancer = applyMiddleware(...middlewares);

if (window.__REDUX_DEVTOOLS_EXTENSION__) {
  enhancer = compose(enhancer, window.__REDUX_DEVTOOLS_EXTENSION__());
} else {
  enhancer = compose(enhancer);
}

window.INITIAL_STATE = window.INITIAL_STATE || {};

const store = createStore(
  rootReducer,
  {
    ...window.INITIAL_STATE,
    pathname: currentHash.replace('#', ''),
  },
  enhancer,
);

store.subscribe(getMarketingFocusUpdater(store, DEFAULT_MARKETING_FOCUS));
store.subscribe(getUserIntentUpdater(store, DEFAULT_USER_INTENT));
store.dispatch(actionCreators.updateIsInternalReferer(getDocumentReferer()));

const impervaScript = document.createElement('script');
impervaScript.setAttribute('src', '/assets/atlas-1-17');
impervaScript.setAttribute('type', 'text/javascript');
impervaScript.async = true;
document.head.appendChild(impervaScript);

const renderer = enableHydrateForSSRRoute(currentHash) ? ReactDOM.hydrate : ReactDOM.render;
renderer(<AppProvider store={store} component={<GeoHierarchyPage />} />, document.getElementById('app'));

document.body.className = '';
document.body.setAttribute('data-atlas-subpage', 'true');

const reportPageView = getReportPageViewWithGeoData(store);
store.subscribe(reportPageView);
reportPageView();
